﻿/**
 * Voxel Quest — curated models from licensed MagicaVoxel sources.
 * Geometry is imported via `npm run import:vox` / `npm run import:year`
 * (see content/models + ATTRIBUTION.md).
 */

import yearCalendarJson from '../../../content/models/year-calendar.json';
import catalogJson from '../../../content/models/catalog.json';
import workshopPoolJson from '../../../content/models/workshop-pool.json';
import IMPORTED from './data/_library.js';
import { buildValidatedSlices } from './slicePipeline.js';
import { MYSTERY_TIER, TIER_MAX_SLICES } from '../config/tiers.js';

/** Build empty N³ volume: voxels[z][y][x] = 0|mat */
export function emptyVolume(size) {
  return Array.from({ length: size }, () =>
    Array.from({ length: size }, () => Array(size).fill(0))
  );
}

export function setVoxel(vol, x, y, z, v = 1) {
  if (x < 0 || y < 0 || z < 0) return;
  const s = vol.length;
  if (x >= s || y >= s || z >= s) return;
  vol[z][y][x] = v;
}

/**
 * Majority-downsample a cubic volume (e.g. 16→8). Keeps a cell if ≥25% of the
 * block is solid; material = mode of non-zero voxels.
 */
export function downsampleVolume(volume, targetSize) {
  const src = volume?.length || 0;
  if (!src || targetSize <= 0 || src % targetSize !== 0) return null;
  const factor = src / targetSize;
  const out = emptyVolume(targetSize);
  const block = factor * factor * factor;
  const need = Math.max(1, Math.floor(block / 4));
  for (let z = 0; z < targetSize; z++) {
    for (let y = 0; y < targetSize; y++) {
      for (let x = 0; x < targetSize; x++) {
        const counts = Object.create(null);
        let filled = 0;
        for (let dz = 0; dz < factor; dz++) {
          for (let dy = 0; dy < factor; dy++) {
            for (let dx = 0; dx < factor; dx++) {
              const v = volume[z * factor + dz][y * factor + dy][x * factor + dx];
              if (!v) continue;
              filled += 1;
              counts[v] = (counts[v] || 0) + 1;
            }
          }
        }
        if (filled < need) continue;
        let best = 0;
        let bestC = 0;
        for (const key of Object.keys(counts)) {
          if (counts[key] > bestC) {
            bestC = counts[key];
            best = Number(key);
          }
        }
        out[z][y][x] = best;
      }
    }
  }
  return out;
}

/** Derive a DP-validated Workshop 8³ model from a 16³ source. */
export function buildWorkshopModelFrom16(source) {
  if (!source?.volume || source.size !== 16) return null;
  const volume = downsampleVolume(source.volume, 8);
  if (!volume) return null;
  const workshopId = String(source.id).endsWith('_16')
    ? String(source.id).replace(/_16$/, '_8')
    : `${source.id}_8`;
  const model = {
    id: workshopId,
    name: source.name,
    category: source.category,
    size: 8,
    answers: source.answers ? [...source.answers] : [],
    volume,
    palette: source.palette ? { ...source.palette } : { 1: '#c4a484' },
    source: source.source || 'Workshop downsample from MagicaVoxel 16³',
    license: source.license || 'See ATTRIBUTION.md',
    pairedAtelierId: source.id,
    tier: MYSTERY_TIER.WORKSHOP,
  };
  const slices = buildValidatedSlices(model, TIER_MAX_SLICES.workshop);
  if (!slices || slices.length < 4) return null;
  model._validatedSliceCount = slices.length;
  return model;
}

/** Minimal heart for bootcamp only — not part of the daily mystery pool. */
export function buildHeart8() {
  const s = 8;
  const vol = emptyVolume(s);
  const layers = [
    '00100100',
    '01111110',
    '01111110',
    '01111110',
    '00111100',
    '00011000',
    '00011000',
    '00000000',
  ];
  for (let z = 2; z <= 5; z++) {
    for (let y = 0; y < 8; y++) {
      for (let x = 0; x < 8; x++) {
        if (layers[y][x] === '1') setVoxel(vol, x, y, z, z === 3 || z === 4 ? 2 : 1);
      }
    }
  }
  return {
    id: 'heart_8_tutorial',
    name: 'Heart',
    category: 'tutorial',
    size: 8,
    answers: ['heart', 'сердце', 'love'],
    volume: vol,
    palette: { 1: '#e74c3c', 2: '#c0392b' },
    source: 'Voxel Quest bootcamp glyph (original tutorial asset)',
    license: 'Original',
  };
}

function cloneModel(model) {
  return structuredClone(model);
}

export const MODEL_CATALOG = Array.isArray(catalogJson) ? catalogJson : [];
export const YEAR_CALENDAR = yearCalendarJson?.days || [];
export const YEAR_THEMES = yearCalendarJson?.themes || [];

/**
 * Curated Workshop sources (16³ ids) — compact-recognizable after 8³ downsample.
 * Regenerated by `npm run curate:workshop`.
 */
export const WORKSHOP_SOURCE_IDS = (() => {
  const fromFile = Array.isArray(workshopPoolJson?.ids) ? workshopPoolJson.ids : [];
  if (fromFile.length >= 8) return fromFile;
  return MODEL_CATALOG.filter((c) => c.workshopEligible && c.size === 16).map((c) => c.id);
})();

/**
 * Year rotation ids — prefer refined year-eligible catalog entries.
 */
export const DAILY_POOL = (() => {
  const eligible = MODEL_CATALOG.filter((c) => c.yearEligible && c.size === 16).map((c) => c.id);
  if (eligible.length >= 16) return eligible;
  const fromYear = [...new Set(YEAR_CALENDAR.map((d) => d.modelId).filter(Boolean))];
  if (fromYear.length >= 16) return fromYear;
  return MODEL_CATALOG.filter((c) => c.ok && c.size === 16).map((c) => c.id);
})();

export const MODEL_LIBRARY = {
  heart_8_tutorial: buildHeart8,
  ...Object.fromEntries(
    Object.entries(IMPORTED || {}).map(([id, model]) => [id, () => cloneModel(model)])
  ),
};

/** Lazy-built Workshop 8³ pool (DP-validated downsamples of Atelier models). */
let _workshopPoolIds = null;
const _workshopModels = new Map();

function getAtelierModel(id) {
  const fn = MODEL_LIBRARY[id];
  return fn ? fn() : null;
}

function ensureWorkshopPool() {
  if (_workshopPoolIds) return _workshopPoolIds;
  const ids = [];
  const sources = WORKSHOP_SOURCE_IDS.length
    ? WORKSHOP_SOURCE_IDS
    : MODEL_CATALOG.filter((c) => c.workshopEligible && c.size === 16).map((c) => c.id);
  for (const sid of sources) {
    const src = getAtelierModel(sid);
    if (!src) continue;
    const workshop = buildWorkshopModelFrom16(src);
    if (!workshop) continue;
    _workshopModels.set(workshop.id, workshop);
    MODEL_LIBRARY[workshop.id] = () => cloneModel(workshop);
    ids.push(workshop.id);
  }
  if (ids.length < 4) {
    const heart = buildHeart8();
    heart.id = 'heart_8_workshop';
    heart.category = 'creatures';
    heart.tier = MYSTERY_TIER.WORKSHOP;
    _workshopModels.set(heart.id, heart);
    MODEL_LIBRARY[heart.id] = () => cloneModel(heart);
    ids.push(heart.id);
  }
  _workshopPoolIds = ids;
  return ids;
}

export function listWorkshopModelIds() {
  return ensureWorkshopPool().slice();
}

export function listModelIds() {
  return Object.keys(MODEL_LIBRARY);
}

export function getCatalogEntry(id) {
  return MODEL_CATALOG.find((c) => c.id === id) || null;
}

export function getModelById(id) {
  if (_workshopModels.has(id)) return cloneModel(_workshopModels.get(id));
  const fn = MODEL_LIBRARY[id];
  return fn ? fn() : null;
}

/** UTC date → model id. Pass tier = workshop | atelier. */
export function getDailyModelId(dateStr, tier = MYSTERY_TIER.ATELIER) {
  if (tier === MYSTERY_TIER.WORKSHOP) {
    const pool = ensureWorkshopPool();
    const atelierId = getDailyModelId(dateStr, MYSTERY_TIER.ATELIER);
    const pairedId = String(atelierId).endsWith('_16')
      ? String(atelierId).replace(/_16$/, '_8')
      : `${atelierId}_8`;
    if (pool.includes(pairedId)) return pairedId;
    for (const wid of pool) {
      const m = _workshopModels.get(wid);
      if (m?.pairedAtelierId === atelierId) return wid;
    }
    // Same-category fallback so Workshop stays thematically aligned with Atelier day
    const day = Math.floor(new Date(dateStr + 'T00:00:00Z').getTime() / 86400000);
    const atelierCat =
      getCatalogEntry(atelierId)?.category || getAtelierModel(atelierId)?.category || null;
    if (atelierCat) {
      const sameCat = pool.filter(
        (wid) => (_workshopModels.get(wid)?.category || '') === atelierCat
      );
      if (sameCat.length) return sameCat[Math.abs(day) % sameCat.length];
    }
    return pool[Math.abs(day) % pool.length];
  }

  const hit = YEAR_CALENDAR.find((d) => d.date === dateStr);
  if (hit?.modelId && MODEL_LIBRARY[hit.modelId]) return hit.modelId;

  const day = Math.floor(new Date(dateStr + 'T00:00:00Z').getTime() / 86400000);
  const pool = DAILY_POOL.length ? DAILY_POOL : listModelIds().filter((id) => id.includes('_16'));
  if (!pool.length) return 'cat_16_ephtracy';
  return pool[Math.abs(day) % pool.length];
}

export function getYearDayEntry(dateStr) {
  return YEAR_CALENDAR.find((d) => d.date === dateStr) || null;
}
